import React, { useEffect, useState } from "react";
import { TrendingUp, ShoppingCart, Package, Users, AlertTriangle, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { api, inr } from "@/lib/api";

const CAT_COLORS = ["#2F80ED", "#14B8A6", "#34D8A0", "#0A2540"];

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-black/10 bg-white p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] uppercase tracking-overline text-black/40">{label}</span>
      <Icon className="h-4 w-4 text-[#2F80ED]" />
    </div>
    <div className="mt-4 font-display text-3xl font-bold tracking-tight">{value}</div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [telegram, setTelegram] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/telegram/status").then(({ data }) => setTelegram(data)).catch(() => {});
  }, []);

  const linkTelegram = async () => {
    setLinking(true);
    try {
      const { data } = await api.post("/admin/telegram/link");
      setTelegram({ bot_configured: true, chat_linked: true, chat_id: data.chat_id });
      toast.success("Telegram linked! Check your phone for a test message.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed. Send /start to your bot first.");
    } finally {
      setLinking(false);
    }
  };

  if (!stats) return <div className="min-h-screen animate-pulse bg-black/5" />;

  return (
    <div className="p-6 sm:p-10" data-testid="admin-dashboard">
      <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Overview</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Dashboard</h1>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={TrendingUp} label="Revenue" value={inr(stats.revenue)} testid="stat-revenue" />
        <StatCard icon={ShoppingCart} label="Paid Orders" value={stats.paid_orders} testid="stat-orders" />
        <StatCard icon={Package} label="Products" value={stats.total_products} testid="stat-products" />
        <StatCard icon={Users} label="Customers" value={stats.total_customers} testid="stat-customers" />
      </div>

      {/* Telegram phone alerts */}
      {telegram?.bot_configured && (
        <div className="mt-6 border border-black/10 bg-white p-6" data-testid="telegram-card">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-[#2F80ED]">
                <Send className="h-4 w-4" /> Phone Alerts · Telegram
              </div>
              <div className="mt-2 font-display text-lg font-bold tracking-tight">
                {telegram.chat_linked
                  ? "You're getting instant Telegram alerts for every enquiry & order."
                  : "One-tap link — send /start to your bot, then click Link."}
              </div>
              {!telegram.chat_linked && (
                <p className="mt-1 text-sm text-black/50">
                  Open Telegram → search your bot → send <span className="font-mono">/start</span> → then click the button.
                </p>
              )}
            </div>
            {telegram.chat_linked ? (
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600" data-testid="telegram-linked">
                <CheckCircle className="h-5 w-5" /> Linked
              </div>
            ) : (
              <button
                onClick={linkTelegram}
                disabled={linking}
                className="rounded-full bg-[#0A2540] px-6 py-3 font-mono text-xs uppercase tracking-overline text-white transition-colors hover:bg-[#2F80ED] disabled:opacity-50"
                data-testid="telegram-link-btn"
              >
                {linking ? "Linking..." : "Link My Telegram"}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* Revenue trend */}
        <div className="border border-black/10 bg-white p-6">
          <h2 className="mb-6 font-mono text-xs uppercase tracking-overline text-black/40">Revenue · Last 7 Days</h2>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.revenue_trend}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2F80ED" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#2F80ED" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                stroke="#999"
              />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#999" width={40} />
              <Tooltip
                formatter={(v) => inr(v)}
                contentStyle={{ fontFamily: "JetBrains Mono", fontSize: 12, borderRadius: 0 }}
              />
              <Area type="monotone" dataKey="revenue" stroke="#2F80ED" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Category distribution */}
        <div className="border border-black/10 bg-white p-6">
          <h2 className="mb-6 font-mono text-xs uppercase tracking-overline text-black/40">Products by Category</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.category_distribution}>
              <XAxis dataKey="category" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#999" />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#999" width={30} allowDecimals={false} />
              <Tooltip contentStyle={{ fontFamily: "JetBrains Mono", fontSize: 12, borderRadius: 0 }} />
              <Bar dataKey="count">
                {stats.category_distribution.map((_, i) => (
                  <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Low stock */}
      <div className="mt-6 border border-black/10 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 font-mono text-xs uppercase tracking-overline text-black/40">
          <AlertTriangle className="h-4 w-4 text-[#34D8A0]" /> Low Stock Alerts
        </h2>
        {stats.low_stock.length === 0 ? (
          <p className="text-sm text-black/50">All products well stocked.</p>
        ) : (
          <div className="space-y-2">
            {stats.low_stock.map((p) => (
              <div key={p.slug} className="flex items-center justify-between border-b border-black/5 py-2 text-sm">
                <span>{p.name}</span>
                <span className="font-mono font-bold text-red-500">{p.stock} left</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
